/**
 * Main App component with routing
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { CartProvider } from './contexts/CartContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import Layout from './components/layout/Layout';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ContactPage from './pages/ContactPage';
import UserPage from './pages/UserPage';
import PhoneVerificationPage from './pages/PhoneVerificationPage';
import AdminPage from './pages/AdminPage';
import InitRoles from './pages/InitRoles';
import TermsPage from './pages/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import NotFoundPage from './pages/NotFoundPage';
import { HelpCenterPage, HelpCategoryPage, HelpArticlePage } from './pages/help';
import ProtectedRoute from './components/common/ProtectedRoute';
import FirebaseConfigCheck from './components/common/FirebaseConfigCheck';
import ErrorBoundary from './components/common/ErrorBoundary';

function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ThemeProvider>
          <FirebaseConfigCheck />
          <CurrencyProvider>
            <AuthProvider>
              <CartProvider>
              <Routes>
              <Route path="/" element={<Layout />}>
                {/* Public routes */}
                <Route index element={<HomePage />} />
                <Route path="products" element={<ProductsPage />} />
                <Route path="products/:id" element={<ProductDetailPage />} />
                <Route path="cart" element={<CartPage />} />
                <Route path="login" element={<LoginPage />} />
                <Route path="register" element={<RegisterPage />} />
                <Route path="contact" element={<ContactPage />} />

                {/* Legal pages */}
                <Route path="terms" element={<TermsPage />} />
                <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="refund-policy" element={<RefundPolicyPage />} />

                {/* Help Center routes */}
                <Route path="help" element={<HelpCenterPage />} />
                <Route path="help/category/:slug" element={<HelpCategoryPage />} />
                <Route path="help/article/:slug" element={<HelpArticlePage />} />

                {/* Protected routes - require authentication */}
                <Route
                  path="checkout"
                  element={
                    <ProtectedRoute>
                      <CheckoutPage />
                    </ProtectedRoute>
                  }
                />

                {/* User account dashboard - unified interface */}
                <Route
                  path="user"
                  element={
                    <ProtectedRoute>
                      <UserPage />
                    </ProtectedRoute>
                  }
                />

                {/* Phone verification - requires authentication */}
                <Route
                  path="verify-phone"
                  element={
                    <ProtectedRoute>
                      <PhoneVerificationPage />
                    </ProtectedRoute>
                  }
                />

                {/* Legacy route redirects - backward compatibility */}
                <Route path="profile" element={<Navigate to="/user?tab=overview" replace />} />
                <Route path="orders" element={<Navigate to="/user?tab=orders" replace />} />
                <Route path="downloads" element={<Navigate to="/user?tab=products" replace />} />
                <Route path="dashboard" element={<Navigate to="/user?tab=orders" replace />} />

                {/* Admin routes - require admin permissions */}
                <Route
                  path="admin/*"
                  element={
                    <ProtectedRoute
                      requiredPermissions={[
                        'product:admin',
                        'user:admin',
                        'order:admin',
                        'analytics:admin',
                        'settings:admin',
                        'audit:admin',
                        'help:admin',
                      ]}
                    >
                      <AdminPage />
                    </ProtectedRoute>
                  }
                />

                {/* Temporary: Initialize roles */}
                <Route
                  path="init-roles"
                  element={
                    <ProtectedRoute>
                      <InitRoles />
                    </ProtectedRoute>
                  }
                />

                {/* 404 */}
                <Route path="*" element={<NotFoundPage />} />
              </Route>
              </Routes>
            </CartProvider>
            </AuthProvider>
          </CurrencyProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
